import { useState } from 'react';
import { FileText, Sparkles, ArrowRight, Loader2 } from 'lucide-react';
import * as LucideIcons from 'lucide-react';
import toast from 'react-hot-toast';
import { WORKFLOW_TEMPLATES } from './templates';

export default function WorkflowStarter({ onSelect, api, companyId, workflowId }) {
  const [aiPrompt, setAiPrompt] = useState('');
  const [aiLoading, setAiLoading] = useState(false);
  const [aiError, setAiError] = useState(null);

  const handleBlank = () => {
    onSelect({ type: 'blank' });
  };

  const handleTemplate = (template) => {
    const { nodes, edges } = template.build();
    onSelect({ type: 'template', nodes, edges });
  };

  const handleAI = async () => {
    if (!aiPrompt.trim()) return;
    setAiLoading(true);
    setAiError(null);
    try {
      const data = await api.generateWorkflow(companyId, workflowId, aiPrompt.trim());
      const raw = data.generation || data;
      const generation = typeof raw === 'string' ? JSON.parse(raw) : raw;
      const nodes = generation.nodes || [];
      const edges = generation.edges || [];
      if (nodes.length === 0) {
        setAiError('AI returned an empty workflow. Try a more detailed description.');
        return;
      }
      onSelect({ type: 'template', nodes, edges });
    } catch {
      setAiError('Failed to generate workflow. Please try again.');
    } finally {
      setAiLoading(false);
    }
  };

  return (
    <div className="flex-1 overflow-y-auto bg-gray-50/50 flex items-center justify-center">
      <div className="w-full max-w-3xl mx-auto px-6">
        {/* AI Generate — always visible */}
        <div className="bg-white rounded-2xl border border-gray-200 shadow-sm p-6 mb-6">
          <div className="flex items-center gap-2 mb-4">
            <div className="w-8 h-8 rounded-lg bg-violet-50 flex items-center justify-center">
              <Sparkles className="w-4 h-4 text-violet-500" />
            </div>
            <div>
              <h3 className="text-sm font-semibold text-gray-900">Generate with AI</h3>
              <p className="text-xs text-gray-500">Describe what you want to automate</p>
            </div>
          </div>
          <div className="flex gap-2">
            <textarea
              value={aiPrompt}
              onChange={(e) => setAiPrompt(e.target.value)}
              placeholder="e.g. When a user registers, send them a welcome email and notify the team on Slack..."
              rows={2}
              style={{ maxHeight: 200 }}
              className="flex-1 px-3 py-2.5 text-sm border border-gray-200 rounded-xl bg-gray-50 focus:outline-none focus:ring-2 focus:ring-violet-200 focus:border-violet-400 resize-y"
            />
            <button
              onClick={handleAI}
              disabled={aiLoading || !aiPrompt.trim()}
              className="shrink-0 flex items-center gap-2 px-4 bg-violet-600 text-white text-sm font-medium rounded-xl hover:bg-violet-700 disabled:bg-gray-300 disabled:cursor-not-allowed transition shadow-sm"
            >
              {aiLoading ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : (
                <Sparkles className="w-4 h-4" />
              )}
              {aiLoading ? 'Generating...' : 'Generate'}
            </button>
          </div>
          {aiError && (
            <p className="text-xs text-red-500 mt-2">{aiError}</p>
          )}
        </div>

        {/* Divider */}
        <div className="flex items-center gap-3 mb-6">
          <div className="flex-1 h-px bg-gray-200" />
          <span className="text-xs font-medium text-gray-400 uppercase tracking-wide">or start from</span>
          <div className="flex-1 h-px bg-gray-200" />
        </div>

        {/* Blank + Templates grid */}
        <div className="grid grid-cols-3 gap-3">
          {/* Blank — single click */}
          <button
            onClick={handleBlank}
            className="flex flex-col items-center gap-2 p-5 rounded-xl border-2 border-dashed border-gray-200 hover:border-gray-400 hover:bg-gray-50 transition text-center group"
          >
            <div className="w-10 h-10 rounded-xl bg-gray-100 flex items-center justify-center group-hover:bg-gray-200 transition">
              <FileText className="w-5 h-5 text-gray-400 group-hover:text-gray-600 transition" />
            </div>
            <div className="text-sm font-semibold text-gray-700">Blank</div>
            <div className="text-[11px] text-gray-400">Start from scratch</div>
          </button>

          {/* Templates */}
          {WORKFLOW_TEMPLATES.map((tpl) => {
            const Icon = LucideIcons[tpl.icon] || FileText;
            return (
              <button
                key={tpl.key}
                onClick={() => handleTemplate(tpl)}
                className="flex flex-col items-center gap-2 p-5 rounded-xl border border-gray-200 bg-white hover:border-blue-300 hover:bg-blue-50/50 transition text-center group"
              >
                <div className="w-10 h-10 rounded-xl bg-blue-50 flex items-center justify-center group-hover:bg-blue-100 transition">
                  <Icon className="w-5 h-5 text-blue-600" />
                </div>
                <div className="text-sm font-semibold text-gray-800">{tpl.label}</div>
                <div className="text-[11px] text-gray-400 line-clamp-2">{tpl.description}</div>
              </button>
            );
          })}
        </div>
      </div>
    </div>
  );
}
